/**
 * MockupOutlineModal — "Sketch outline mockup" on a tracker entry (TrackerWorkspace.jsx).
 *
 * A simple mini-outliner for sketching a *proposed* new outline node hierarchy (e.g. "add a
 * Warranty Claims section under Reference, with these children") when filing a Feature Request or
 * Suggestion. Captures shape only — one row per node: its nesting depth, its kind, and a free-text
 * label. `kind` is the exact vocabulary this codebase already uses for outline nodes (not a new
 * taxonomy):
 *   - section — a category wrapper (e.g. "Reference", "Threads") — children-only zoom, no own record
 *   - entity  — a real record (e.g. a Milestone, a Room) — full detail zoom + breadcrumb + ⋯ menu
 *   - leaf    — a plain row, no children, no zoom
 *
 * Stages the tree in local state only — like `images` in EntryFormModal, nothing is written to the
 * DB here. The parent form reads the final `nodes` array via `onSave` and includes it in the
 * entry's `meta.mockup_outline` when the outer form is saved. See api/tracker-prompt.js for how
 * this shape drives the generated Claude Code prompt (a deterministic "Outline wiring checklist"
 * section is appended whenever an entry carries a mockup — never left for the model to remember).
 */

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const KIND_ORDER = ['section', 'entity', 'leaf']
const KIND_COLOR = { section: '#1D6FB8', entity: '#1D9E75', leaf: '#7a9aaa' }
const KIND_LABEL = { section: 'Section', entity: 'Entity', leaf: 'Leaf' }

let _seq = 0
function newId() { _seq += 1; return 'n' + Date.now() + '_' + _seq }

function KindPill({ kind, onClick }) {
  const color = KIND_COLOR[kind] || '#7a9aaa'
  return (
    <button onClick={onClick} title="Click to change node kind" style={{
      fontFamily: '"DM Mono",monospace', fontSize: 10, color, background: color + '18',
      borderRadius: 4, padding: '2px 7px', whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', flexShrink: 0,
    }}>{KIND_LABEL[kind] || kind}</button>
  )
}

export default function MockupOutlineModal({ initialNodes, onClose, onSave }) {
  const [nodes, setNodes] = useState(() => (initialNodes && initialNodes.length ? initialNodes : [{ id: newId(), depth: 0, kind: 'section', label: '' }]))
  const [focusId, setFocusId] = useState(null)
  const inputRefs = useRef({})

  useEffect(() => {
    if (focusId && inputRefs.current[focusId]) { inputRefs.current[focusId].focus(); setFocusId(null) }
  }, [focusId])

  function subtreeEnd(index) {
    const depth = nodes[index].depth
    let end = index + 1
    while (end < nodes.length && nodes[end].depth > depth) end += 1
    return end
  }

  function updateLabel(index, value) {
    setNodes(prev => prev.map((n, i) => (i === index ? { ...n, label: value } : n)))
  }

  function cycleKind(index) {
    setNodes(prev => prev.map((n, i) => {
      if (i !== index) return n
      const next = KIND_ORDER[(KIND_ORDER.indexOf(n.kind) + 1) % KIND_ORDER.length]
      return { ...n, kind: next }
    }))
  }

  function indent(index) {
    if (index === 0) return
    setNodes(prev => {
      const maxDepth = prev[index - 1].depth + 1
      if (prev[index].depth >= maxDepth) return prev
      return prev.map((n, i) => (i === index ? { ...n, depth: n.depth + 1 } : n))
    })
  }

  function outdent(index) {
    setNodes(prev => {
      if (prev[index].depth <= 0) return prev
      return prev.map((n, i) => (i === index ? { ...n, depth: n.depth - 1 } : n))
    })
  }

  function addSiblingBelow(index) {
    const id = newId()
    setNodes(prev => {
      const depth = prev[index].depth
      const insertAt = subtreeEnd(index)
      const copy = prev.slice()
      copy.splice(insertAt, 0, { id, depth, kind: 'leaf', label: '' })
      return copy
    })
    setFocusId(id)
  }

  function addRootNode() {
    const id = newId()
    setNodes(prev => [...prev, { id, depth: 0, kind: 'section', label: '' }])
    setFocusId(id)
  }

  function deleteNode(index) {
    setNodes(prev => {
      if (prev.length <= 1) return prev
      const end = subtreeEnd(index)
      return prev.slice(0, index).concat(prev.slice(end))
    })
  }

  function handleKeyDown(e, index) {
    if (e.key === 'Enter') { e.preventDefault(); addSiblingBelow(index) }
    else if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); indent(index) }
    else if (e.key === 'Tab' && e.shiftKey) { e.preventDefault(); outdent(index) }
  }

  function done() {
    onSave(nodes.filter(n => n.label.trim()))
  }

  return createPortal(
    <div style={S.overlay}>
      <div data-modal-id="MockupOutlineModal" style={S.card} role="dialog" aria-modal="true">
        <div style={S.head}>
          <span style={S.title}>Sketch outline mockup</span>
          <button onClick={onClose} style={S.x}>×</button>
        </div>
        <div style={S.body}>
          <div style={S.hint}>
            One row per proposed outline node. Tab/Shift+Tab (or the arrows) to nest, Enter for a
            new row, click the pill to set what kind of node it is.
          </div>
          <div style={S.rows}>
            {nodes.map((n, i) => (
              <div key={n.id} style={{ ...S.row, paddingLeft: 8 + n.depth * 22 }}>
                <span style={S.arrows}>
                  <button style={S.arrowBtn} title="Outdent" onClick={() => outdent(i)} disabled={n.depth === 0}>◀</button>
                  <button style={S.arrowBtn} title="Indent" onClick={() => indent(i)} disabled={i === 0}>▶</button>
                </span>
                <KindPill kind={n.kind} onClick={() => cycleKind(i)} />
                <input
                  ref={el => { inputRefs.current[n.id] = el }}
                  value={n.label}
                  placeholder="Node label…"
                  style={S.input}
                  onChange={e => updateLabel(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(e, i)}
                />
                <button style={S.del} title="Delete node (and its children)" onClick={() => deleteNode(i)}>×</button>
              </div>
            ))}
          </div>
          <button style={S.addRoot} onClick={addRootNode}>+ Add root node</button>
        </div>
        <div style={S.foot}>
          <button onClick={onClose} style={S.ghost}>Cancel</button>
          <button onClick={done} style={S.primary}>Done</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 800, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' },
  card: { width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.3)' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  title: { fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13, fontWeight: 700, color: 'var(--aj-text)' },
  x: { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer' },
  body: { padding: '14px 18px', flex: '1 1 auto', minHeight: 0, overflowY: 'auto' },
  hint: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', marginBottom: 10, lineHeight: 1.4 },
  rows: { display: 'flex', flexDirection: 'column', gap: 4 },
  row: { display: 'flex', alignItems: 'center', gap: 6 },
  arrows: { display: 'flex', gap: 1, flexShrink: 0 },
  arrowBtn: { background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 3, width: 18, height: 18, lineHeight: 1, fontSize: 9, color: 'var(--aj-text-mid)', cursor: 'pointer', padding: 0 },
  input: { flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' },
  del: { flexShrink: 0, background: 'none', border: 'none', fontSize: 15, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer', padding: '0 2px' },
  addRoot: { marginTop: 10, background: 'none', border: '0.5px dashed var(--aj-border-mid)', borderRadius: 4, padding: '6px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', cursor: 'pointer' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  primary: { background: '#07272D', border: 'none', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' },
  ghost: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' },
}
